import React, { useEffect, useState, useCallback } from "react";
import { db } from "../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import {
  FaArrowLeft,
  FaWallet,
  FaUsers,
  FaMoneyBillWave,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaSyncAlt,
  FaChevronRight,
  FaUser,
} from "react-icons/fa";

import "./NewDeposit.css";


/* =========================================================
   SETTINGS
========================================================= */

const CACHE_TIME = 2 * 60 * 1000;

const MAX_LEVEL = 3;

const MAX_BATCH = 10;

const DEPOSIT_COLLECTIONS = [
  "tsh_transactions",
  "usdt_transactions",
  "vistodeposit",
];


/* =========================================================
   CACHE
========================================================= */

const getCacheKey = (username) =>
  `newDepositCache_${username}`;


/* =========================================================
   DATE HELPERS
========================================================= */

const getDateValue = (value) => {
  if (!value) return null;

  try {
    if (value?.toDate) {
      return value.toDate();
    }

    if (value?.seconds) {
      return new Date(value.seconds * 1000);
    }

    if (value instanceof Date) {
      return value;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime())
      ? null
      : date;

  } catch {
    return null;
  }
};


const isToday = (value) => {

  const date = getDateValue(value);

  if (!date) return false;

  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};


const formatTime = (value) => {

  const date = getDateValue(value);

  if (!date) {
    return "--:--";
  }

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};


/* =========================================================
   AMOUNT
========================================================= */

const formatAmount = (amount) => {

  const number = Number(amount || 0);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return number.toLocaleString();
};


/* =========================================================
   NORMALIZE STATUS
========================================================= */

const normalizeStatus = (status) => {

  if (!status) {
    return "pending";
  }

  const value =
    String(status)
      .trim()
      .toLowerCase();


  if (
    [
      "success",
      "successful",
      "completed",
      "complete",
      "paid",
      "approved",
    ].includes(value)
  ) {
    return "success";
  }


  if (
    [
      "failed",
      "failure",
      "cancelled",
      "canceled",
      "rejected",
      "declined",
    ].includes(value)
  ) {
    return "failed";
  }


  return "pending";
};


/* =========================================================
   FIELD HELPERS
========================================================= */

const getUsername = (data) => {

  return (
    data?.username ||
    data?.userName ||
    data?.user ||
    data?.name ||
    ""
  );
};


const getAmount = (data) => {

  return (
    data?.amount ??
    data?.depositAmount ??
    data?.transactionAmount ??
    data?.paymentAmount ??
    data?.value ??
    data?.betAmount ??
    0
  );
};


const getCurrency = (
  data,
  collectionName
) => {

  if (data?.currency) {
    return String(
      data.currency
    ).toUpperCase();
  }


  if (data?.currencyCode) {
    return String(
      data.currencyCode
    ).toUpperCase();
  }


  if (data?.coin) {
    return String(
      data.coin
    ).toUpperCase();
  }


  if (
    collectionName ===
    "usdt_transactions"
  ) {
    return "USDT";
  }


  return "TZS";
};


const getCreatedAt = (data) => {

  return (
    data?.createdAt ||
    data?.timestamp ||
    data?.date ||
    data?.transactionDate ||
    data?.created ||
    data?.time ||
    null
  );
};


/* =========================================================
   STATUS ICON
========================================================= */

const StatusIcon = ({
  status,
}) => {

  if (status === "success") {
    return <FaCheckCircle />;
  }

  if (status === "failed") {
    return <FaTimesCircle />;
  }

  return <FaHourglassHalf />;
};


/* =========================================================
   CHUNK ARRAY
========================================================= */

const chunkArray = (
  array,
  size = MAX_BATCH
) => {

  const chunks = [];

  for (
    let i = 0;
    i < array.length;
    i += size
  ) {

    chunks.push(
      array.slice(
        i,
        i + size
      )
    );

  }

  return chunks;
};


/* =========================================================
   NORMALIZE DEPOSIT
========================================================= */

const normalizeDeposit = (
  doc,
  collectionName
) => {

  const data = doc.data();

  const createdAt =
    getCreatedAt(data);


  if (!isToday(createdAt)) {
    return null;
  }


  return {

    id:
      `${collectionName}_${doc.id}`,

    originalId:
      doc.id,

    username:
      getUsername(data),

    amount:
      Number(
        getAmount(data) || 0
      ),

    currency:
      getCurrency(
        data,
        collectionName
      ),

    status:
      normalizeStatus(
        data.status
      ),

    createdAt,

    source:
      collectionName,

  };
};


/* =========================================================
   NEW DEPOSIT
========================================================= */

const NewDeposit = () => {

  const navigate =
    useNavigate();


  const [
    levels,
    setLevels,
  ] = useState({
    1: [],
    2: [],
    3: [],
  });


  const [
    activeLevel,
    setActiveLevel,
  ] = useState(1);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    refreshing,
    setRefreshing,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  const loggedInUsername =
    localStorage.getItem(
      "username"
    );


  /* =======================================================
     SAVE CACHE
  ======================================================= */

  const saveCache =
    useCallback(
      (data) => {

        if (!loggedInUsername) {
          return;
        }

        try {

          localStorage.setItem(

            getCacheKey(
              loggedInUsername
            ),

            JSON.stringify({
              data,
              updatedAt:
                Date.now(),
            })

          );

        } catch (err) {

          console.error(
            "Cache save error:",
            err
          );

        }

      },
      [loggedInUsername]
    );


  /* =======================================================
     LOAD CACHE
  ======================================================= */

  const loadCache =
    useCallback(
      () => {

        if (!loggedInUsername) {
          return null;
        }

        try {

          const raw =
            localStorage.getItem(
              getCacheKey(
                loggedInUsername
              )
            );


          if (!raw) {
            return null;
          }


          const cached =
            JSON.parse(raw);


          if (
            !cached ||
            !cached.data
          ) {
            return null;
          }


          return cached;

        } catch {

          return null;

        }

      },
      [loggedInUsername]
    );


  /* =======================================================
     FETCH CURRENT USER
  ======================================================= */

  const fetchCurrentUser =
    useCallback(
      async () => {

        if (!loggedInUsername) {
          return null;
        }


        const q =
          query(
            collection(
              db,
              "users"
            ),

            where(
              "username",
              "==",
              loggedInUsername
            ),

            limit(1)
          );


        const snap =
          await getDocs(q);


        if (snap.empty) {
          return null;
        }


        const doc =
          snap.docs[0];


        return {
          id: doc.id,
          ...doc.data(),
        };

      },
      [loggedInUsername]
    );


  /* =======================================================
     FETCH USERS BY REFERRAL CODES
  ======================================================= */

  const fetchUsersByCodes =
    async (
      referralCodes
    ) => {

      if (
        !referralCodes ||
        referralCodes.length === 0
      ) {
        return [];
      }


      const uniqueCodes =
        [
          ...new Set(
            referralCodes
              .filter(Boolean)
          ),
        ];


      const chunks =
        chunkArray(
          uniqueCodes
        );


      const snapshots =
        await Promise.all(

          chunks.map(
            async (chunk) => {

              try {

                const q =
                  query(
                    collection(
                      db,
                      "users"
                    ),

                    where(
                      "whoReferredWho",
                      "in",
                      chunk
                    )
                  );


                return await getDocs(
                  q
                );

              } catch (err) {

                console.error(
                  "Users batch error:",
                  err
                );

                return null;

              }

            }
          )

        );


      const users = [];


      snapshots.forEach(
        (snap) => {

          if (!snap) {
            return;
          }


          snap.forEach(
            (doc) => {

              users.push({
                id: doc.id,
                ...doc.data(),
              });

            }
          );

        }
      );


      /*
       * Remove duplicates
       */

      return Array.from(
        new Map(
          users.map(
            (user) => [
              user.id,
              user,
            ]
          )
        ).values()
      );

    };


  /* =======================================================
     FETCH DEPOSITS FOR USERS — FAST BATCH
  ======================================================= */

  const fetchDepositsForUsers =
    async (
      usernames
    ) => {

      const uniqueUsernames =
        [
          ...new Set(
            usernames
              .filter(Boolean)
          ),
        ];


      if (
        uniqueUsernames.length === 0
      ) {
        return new Map();
      }


      /*
       * Create map:
       * username => deposits[]
       */

      const depositMap =
        new Map();


      uniqueUsernames.forEach(
        (username) => {

          depositMap.set(
            username,
            []
          );

        }
      );


      /*
       * Firestore "in" max = 10.
       */

      const chunks =
        chunkArray(
          uniqueUsernames
        );


      /*
       * READ ALL 3 COLLECTIONS
       * AT THE SAME TIME.
       */

      await Promise.all(

        DEPOSIT_COLLECTIONS.map(
          async (
            collectionName
          ) => {

            /*
             * Each collection may need
             * multiple batches.
             */

            await Promise.all(

              chunks.map(
                async (
                  usernameChunk
                ) => {

                  try {

                    const q =
                      query(
                        collection(
                          db,
                          collectionName
                        ),

                        where(
                          "username",
                          "in",
                          usernameChunk
                        )
                      );


                    const snap =
                      await getDocs(
                        q
                      );


                    snap.forEach(
                      (doc) => {

                        const deposit =
                          normalizeDeposit(
                            doc,
                            collectionName
                          );


                        if (!deposit) {
                          return;
                        }


                        /*
                         * Some collections may
                         * use another username field.
                         * Prefer stored username.
                         */

                        const username =
                          deposit.username;


                        if (
                          !username
                        ) {
                          return;
                        }


                        if (
                          !depositMap.has(
                            username
                          )
                        ) {

                          depositMap.set(
                            username,
                            []
                          );

                        }


                        depositMap
                          .get(username)
                          .push(
                            deposit
                          );

                      }
                    );


                  } catch (err) {

                    console.error(
                      `Deposit query error: ${collectionName}`,
                      err
                    );

                  }

                }
              )

            );

          }
        )

      );


      return depositMap;

    };


  /* =======================================================
     FETCH ALL LEVELS
  ======================================================= */

  const fetchLevelData =
    async (
      referralCode
    ) => {

      const result = {
        1: [],
        2: [],
        3: [],
      };


      let currentCodes = [
        referralCode,
      ];


      for (
        let level = 1;
        level <= MAX_LEVEL;
        level++
      ) {

        if (
          currentCodes.length === 0
        ) {
          break;
        }


        /*
         * GET USERS FOR THIS LEVEL
         */

        const users =
          await fetchUsersByCodes(
            currentCodes
          );


        if (
          users.length === 0
        ) {

          result[level] = [];

          break;

        }


        /*
         * GET ALL USERNAMES
         */

        const usernames =
          users
            .map(
              (user) =>
                user.username
            )
            .filter(Boolean);


        /*
         * ONE BATCH SYSTEM FOR
         * ALL DEPOSITS.
         */

        const depositMap =
          await fetchDepositsForUsers(
            usernames
          );


        /*
         * Attach deposits.
         */

        const usersWithDeposits =
          users
            .map(
              (user) => {

                const deposits =
                  depositMap.get(
                    user.username
                  ) || [];


                /*
                 * Sort newest first.
                 */

                deposits.sort(
                  (a, b) => {

                    const dateA =
                      getDateValue(
                        a.createdAt
                      );

                    const dateB =
                      getDateValue(
                        b.createdAt
                      );


                    return (
                      (
                        dateB?.getTime() ||
                        0
                      ) -
                      (
                        dateA?.getTime() ||
                        0
                      )
                    );

                  }
                );


                const successful =
                  deposits.filter(
                    (deposit) =>
                      deposit.status ===
                      "success"
                  );


                const depositTotal =
                  successful.reduce(
                    (
                      total,
                      deposit
                    ) =>
                      total +
                      Number(
                        deposit.amount ||
                          0
                      ),
                    0
                  );


                return {

                  ...user,

                  username:
                    user.username ||
                    "Unknown",

                  deposits,

                  depositTotal,

                };

              }
            )
            .filter(
              (user) =>
                user.deposits.length > 0
            );


        result[level] =
          usersWithDeposits;


        /*
         * PREPARE NEXT LEVEL
         */

        currentCodes =
          users
            .map(
              (user) =>
                user.referralCode
            )
            .filter(Boolean);

      }


      return result;

    };


  /* =======================================================
     LOAD
  ======================================================= */

  const loadDeposits =
    useCallback(
      async (
        showRefresh = false
      ) => {

        if (!loggedInUsername) {

          setLoading(false);

          return;

        }


        if (showRefresh) {
          setRefreshing(true);
        }


        setError("");


        try {

          const user =
            await fetchCurrentUser();


          if (
            !user ||
            !user.referralCode
          ) {

            const empty = {
              1: [],
              2: [],
              3: [],
            };


            setLevels(empty);

            saveCache(empty);

            return;

          }


          const data =
            await fetchLevelData(
              user.referralCode
            );


          /*
           * Update UI immediately.
           */

          setLevels(data);


          /*
           * Save for next opening.
           */

          saveCache(data);


        } catch (err) {

          console.error(
            "New Deposit error:",
            err
          );


          setError(
            "Failed to load deposits."
          );

        } finally {

          setLoading(false);

          setRefreshing(false);

        }

      },
      [
        loggedInUsername,
        fetchCurrentUser,
        saveCache,
      ]
    );


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {

    if (!loggedInUsername) {

      setLoading(false);

      return;

    }


    /*
     * CACHE FIRST
     */

    const cached =
      loadCache();


    if (cached?.data) {

      setLevels({
        1:
          cached.data[1] || [],

        2:
          cached.data[2] || [],

        3:
          cached.data[3] || [],
      });


      /*
       * OPEN UI IMMEDIATELY.
       */

      setLoading(false);

    }


    /*
     * FIRESTORE BACKGROUND UPDATE
     */

    loadDeposits(false);

  }, [
    loggedInUsername,
    loadCache,
    loadDeposits,
  ]);


  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(() => {

    if (!loggedInUsername) {
      return;
    }


    const interval =
      setInterval(
        () => {

          loadDeposits(
            false
          );

        },
        CACHE_TIME
      );


    return () => {
      clearInterval(
        interval
      );
    };

  }, [
    loggedInUsername,
    loadDeposits,
  ]);


  /* =======================================================
     CALCULATIONS
  ======================================================= */

  const currentUsers =
    levels[
      activeLevel
    ] || [];


  const allDeposits =
    currentUsers.flatMap(
      (user) =>
        user.deposits || []
    );


  const successfulDeposits =
    allDeposits.filter(
      (deposit) =>
        deposit.status ===
        "success"
    );


  const totalDeposit =
    successfulDeposits.reduce(
      (
        total,
        deposit
      ) =>
        total +
        Number(
          deposit.amount || 0
        ),
      0
    );


  const totalDeposits =
    allDeposits.length;


  const totalMembers =
    Object.values(
      levels
    ).reduce(
      (
        total,
        users
      ) =>
        total +
        users.length,
      0
    );


  /* =======================================================
     LOGIN SCREEN
  ======================================================= */

  if (!loggedInUsername) {

    return (

      <div className="deposit-wrapper">

        <div className="deposit-empty">

          <div className="deposit-empty-icon">
            <FaUser />
          </div>

          <h3>
            Login Required
          </h3>

          <p>
            Please login first to view
            your team's new deposits.
          </p>

          <button
            type="button"
            className="deposit-back-empty"
            onClick={() =>
              navigate(-1)
            }
          >

            <FaArrowLeft />

            Back

          </button>

        </div>

      </div>

    );

  }


  /* =======================================================
     LOADING SCREEN
  ======================================================= */

  if (loading) {

    return (

      <div className="deposit-wrapper">

        <div className="deposit-loading">

          <div className="deposit-spinner" />

          <strong>
            Loading new deposits
          </strong>

          <span>
            Checking today's transactions...
          </span>

        </div>

      </div>

    );

  }


  /* =======================================================
     MAIN UI
  ======================================================= */

  return (

    <div className="deposit-wrapper">


      {/* HEADER */}

      <div className="deposit-header">

        <div className="deposit-header-left">

          <button
            type="button"
            className="deposit-back-btn"
            onClick={() =>
              navigate(-1)
            }
          >

            <FaArrowLeft />

            <span>
              Back
            </span>

          </button>


          <div className="deposit-title">

            <div className="deposit-title-icon">
              <FaWallet />
            </div>

            <div>

              <span>
                TEAM ACTIVITY
              </span>

              <h1>
                New Deposit
              </h1>

            </div>

          </div>

        </div>


        <button
          type="button"
          className={
            refreshing
              ? "deposit-refresh spinning"
              : "deposit-refresh"
          }
          onClick={() =>
            loadDeposits(true)
          }
        >

          <FaSyncAlt />

        </button>

      </div>


      {/* ERROR */}

      {error && (

        <div className="deposit-error">

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={() =>
              loadDeposits(true)
            }
          >
            Retry
          </button>

        </div>

      )}


      {/* SUMMARY */}

      <div className="deposit-summary">

        <div className="deposit-summary-icon">

          <FaMoneyBillWave />

        </div>


        <div className="deposit-summary-main">

          <span>
            TODAY'S SUCCESSFUL DEPOSITS
          </span>

          <strong>
            {formatAmount(
              totalDeposit
            )}
          </strong>

          <small>
            Across your team
          </small>

        </div>


        <div className="deposit-summary-side">

          <strong>
            {totalDeposits}
          </strong>

          <span>
            Deposits
          </span>

        </div>

      </div>


      {/* STATS */}

      <div className="deposit-stats">


        <div className="deposit-stat">

          <div className="deposit-stat-icon">
            <FaUsers />
          </div>

          <div>

            <span>
              MEMBERS
            </span>

            <strong>
              {totalMembers}
            </strong>

          </div>

        </div>


        <div className="deposit-stat">

          <div className="deposit-stat-icon">
            <FaCheckCircle />
          </div>

          <div>

            <span>
              SUCCESS
            </span>

            <strong>
              {successfulDeposits.length}
            </strong>

          </div>

        </div>


        <div className="deposit-stat">

          <div className="deposit-stat-icon">
            <FaClock />
          </div>

          <div>

            <span>
              LEVEL
            </span>

            <strong>
              {activeLevel}
            </strong>

          </div>

        </div>

      </div>


      {/* LEVEL TABS */}

      <div className="deposit-level-tabs">

        {[1, 2, 3].map(
          (level) => {

            const count =
              levels[level]?.length ||
              0;


            return (

              <button
                key={level}
                type="button"
                className={
                  activeLevel === level
                    ? "deposit-level-tab active"
                    : "deposit-level-tab"
                }
                onClick={() =>
                  setActiveLevel(
                    level
                  )
                }
              >

                <span>
                  LEVEL {level}
                </span>

                <strong>
                  {count}
                </strong>

              </button>

            );

          }
        )}

      </div>


      {/* SECTION HEADER */}

      <div className="deposit-section-head">

        <div>

          <span>
            TODAY'S DEPOSITS
          </span>

          <strong>
            Level {activeLevel}
          </strong>

        </div>


        <div className="deposit-section-count">

          {currentUsers.length}

          <span>
            members
          </span>

        </div>

      </div>


      {/* EMPTY */}

      {currentUsers.length === 0 ? (

        <div className="deposit-empty">

          <div className="deposit-empty-icon">

            <FaWallet />

          </div>

          <h3>
            No New Deposits
          </h3>

          <p>
            There are no deposits from
            Level {activeLevel} today.
          </p>

          <div className="deposit-empty-date">

            <FaClock />

            <span>
              Checked today
            </span>

          </div>

        </div>

      ) : (

        /* =================================================
           USERS
        ================================================= */

        <div className="deposit-list">

          {currentUsers.map(
            (user, index) => {

              const deposits =
                user.deposits || [];


              return (

                <div
                  key={user.id}
                  className="deposit-user-card"
                >


                  {/* USER HEADER */}

                  <div className="deposit-user-header">

                    <div className="deposit-number">

                      {String(
                        index + 1
                      ).padStart(
                        2,
                        "0"
                      )}

                    </div>


                    <div className="deposit-avatar">

                      <FaUser />

                    </div>


                    <div className="deposit-user-info">

                      <strong>
                        {user.username ||
                          "Unknown"}
                      </strong>

                      <span>
                        Level {activeLevel}
                      </span>

                    </div>


                    <div className="deposit-user-total">

                      <span>
                        TOTAL
                      </span>

                      <strong>
                        {formatAmount(
                          user.depositTotal
                        )}
                      </strong>

                    </div>

                  </div>


                  {/* DEPOSIT TRANSACTIONS */}

                  <div className="deposit-transactions">

                    {deposits.map(
                      (deposit) => {

                        const status =
                          deposit.status;


                        return (

                          <div
                            key={
                              deposit.id
                            }
                            className="deposit-row"
                          >


                            <div className="deposit-row-icon">

                              <FaWallet />

                            </div>


                            <div className="deposit-row-info">

                              <strong>

                                {formatAmount(
                                  deposit.amount
                                )}

                                {" "}

                                {deposit.currency}

                              </strong>


                              <div className="deposit-row-meta">

                                <span>

                                  <FaClock />

                                  {formatTime(
                                    deposit.createdAt
                                  )}

                                </span>


                                <span>

                                  {deposit.source ===
                                  "tsh_transactions"

                                    ? "TZS"

                                    : deposit.source ===
                                      "usdt_transactions"

                                    ? "USDT"

                                    : "VISTO"}

                                </span>

                              </div>

                            </div>


                            <div
                              className={
                                `deposit-status ${status}`
                              }
                            >

                              <StatusIcon
                                status={
                                  status
                                }
                              />

                              <span>

                                {status ===
                                "success"

                                  ? "SUCCESS"

                                  : status ===
                                    "failed"

                                  ? "FAILED"

                                  : "PENDING"}

                              </span>

                            </div>


                            <FaChevronRight
                              className="deposit-chevron"
                            />

                          </div>

                        );

                      }
                    )}

                  </div>

                </div>

              );

            }
          )}

        </div>

      )}


      {/* FOOTER */}

      <div className="deposit-footer">

        <span>
          DEPOSIT ACTIVITY
        </span>

        <small>
          Showing team deposits registered today.
        </small>

      </div>

    </div>

  );

};


export default NewDeposit;
